import {
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
    type CSSProperties,
    type ReactNode,
} from "react";
import { gameGlyph, gameStyle, type CatalogGame, type Profile } from "./api";
import {
    combineRoomName,
    generateRoomName,
    roomAdjectives,
    roomNouns,
    roomPrefixes,
} from "./names";
import { virtualRange } from "./VirtualList";

export function Field({
    label,
    children,
    group = false,
}: {
    label: string;
    children: ReactNode;
    group?: boolean;
}) {
    if (group) return <div className="guild-field" role="group" aria-label={label}><span>{label}</span>{children}</div>;
    return (
        <label className="guild-field">
            <span>{label}</span>
            {children}
        </label>
    );
}
export function Dialog({
    title,
    children,
    close,
    wide = false,
}: {
    title: string;
    children: ReactNode;
    close: () => void;
    wide?: boolean;
}) {
    const ref = useRef<HTMLDialogElement>(null);
    const invoker = useRef<HTMLElement | null>(typeof document !== 'undefined' && document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const id = useId();
    useEffect(() => {
        const dialog = ref.current!;
        dialog.showModal();
        return () => { dialog.close(); if (invoker.current?.isConnected) invoker.current.focus({ preventScroll: true }); };
    }, []);
    return (
        <dialog
            ref={ref}
            className={`guild-dialog ${wide ? "wide" : ""}`}
            onCancel={event => { event.preventDefault(); close(); }}
            aria-labelledby={id}
        >
            <div className="guild-dialog-head">
                <h2 id={id}>{title}</h2>
                <button type="button" aria-label="Close dialog" onClick={close}>
                    ×
                </button>
            </div>
            <div className="guild-dialog-content">{children}</div>
        </dialog>
    );
}
export function Avatar({
    profile,
    name = "Guest",
    bot = false,
    open = false,
    large = false,
}: {
    profile?: Profile;
    name?: string;
    bot?: boolean;
    open?: boolean;
    large?: boolean;
}) {
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [profile?.iconUrl]);
    return (
        <span
            className={`guild-avatar ${large ? "large" : ""} ${open ? "open" : ""}`}
            style={
                {
                    "--pa": profile?.primaryColor ?? "#8b7056",
                    "--pb": profile?.secondaryColor ?? "#d4ae62",
                } as CSSProperties
            }
            title={name}
        >
            {profile?.iconUrl && !failed ? (
                <img
                    src={profile.iconUrl}
                    alt=""
                    onError={() => setFailed(true)}
                />
            ) : open ? (
                "+"
            ) : bot ? (
                "⚙"
            ) : (
                name.slice(0, 1).toUpperCase()
            )}
        </span>
    );
}
export function GameShield({
    game,
}: {
    game: Pick<CatalogGame, "id" | "title">;
}) {
    return (
        <span
            className={`guild-shield shield-${gameStyle(game.id)}`}
            aria-hidden="true"
        >
            <span>{gameGlyph(game.id)}</span>
        </span>
    );
}
export function SectionTitle({
    title,
    children,
}: {
    title: string;
    children?: ReactNode;
}) {
    return (
        <div className="guild-section-title">
            <h2>{title}</h2>
            {children}
        </div>
    );
}

/** Selection and filter text are separate: typing cannot silently change a selected word. */
export function WordCombo({
    label,
    words,
    value,
    onChange,
    optional = false,
}: {
    label: string;
    words: string[];
    value: string;
    onChange: (value: string) => void;
    optional?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [active, setActive] = useState(0);
    const id = useId();
    const options = (optional ? ["", ...words] : words).filter((w) =>
        (w || "None").toLowerCase().includes(search.toLowerCase()),
    );
    const optionsRef = useRef<HTMLDivElement>(null);
    const [optionTop,setOptionTop] = useState(0);
    const range = virtualRange(options.length,optionTop,180,28,2);
    useLayoutEffect(()=>{const element=optionsRef.current;if(!element)return;if(active*28<element.scrollTop)element.scrollTop=active*28;else if((active+1)*28>element.scrollTop+element.clientHeight)element.scrollTop=(active+1)*28-element.clientHeight;setOptionTop(element.scrollTop);},[active,search,open]);
    const choose = (word: string) => {
        onChange(word);
        setOpen(false);
        setSearch("");
        setActive(0);
    };
    return (
        <div className="guild-word-combo">
            <label htmlFor={id}>{label}</label>
            <div className="guild-combo-input">
                <input
                    id={id}
                    role="combobox"
                    aria-expanded={open}
                    aria-controls={`${id}-options`}
                    aria-autocomplete="list"
                    aria-activedescendant={
                        open && options.length
                            ? `${id}-option-${active}`
                            : undefined
                    }
                    value={open ? search : value || "None"}
                    onFocus={() => {
                        setSearch("");
                        setOpen(true);
                    }}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setActive(0);
                        setOpen(true);
                    }}
                    onBlur={() => {
                        setOpen(false);
                        setSearch("");
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") {
                            e.preventDefault();
                            setOpen(false);
                            setSearch("");
                        } else if (
                            e.key === "ArrowDown" ||
                            e.key === "ArrowUp"
                        ) {
                            e.preventDefault();
                            setOpen(true);
                            setActive((i) =>
                                Math.max(
                                    0,
                                    Math.min(
                                        options.length - 1,
                                        i + (e.key === "ArrowDown" ? 1 : -1),
                                    ),
                                ),
                            );
                        } else if (e.key === "Enter" && open) {
                            e.preventDefault();
                            if (options[active] !== undefined)
                                choose(options[active]!);
                        }
                    }}
                />
                <span aria-hidden="true">⌄</span>
            </div>
            {open && (
                <div ref={optionsRef} className="guild-word-options" id={`${id}-options`} role="listbox" onScroll={event=>setOptionTop(event.currentTarget.scrollTop)}><div style={{height:Math.max(28,options.length*28),position:"relative"}}>
                    {options.slice(range.start,range.end).map((word,offset)=>{const index=range.start+offset;return <div role="option" id={`${id}-option-${index}`} aria-selected={word===value} aria-posinset={index+1} aria-setsize={options.length} className={index===active?"active":""} key={word} style={{position:"absolute",top:index*28,height:28,left:0,right:0,padding:"5px 7px"}} onMouseDown={event=>{event.preventDefault();choose(word);}}>{word||"None"}</div>;})}
                    {!options.length && <div className="guild-muted" style={{padding:7}}>No matching words</div>}
                </div></div>

            )}
        </div>
    );
}
export function RoomNameBuilder({
    onName,
}: {
    onName: (name: string) => void;
}) {
    const [parts, setParts] = useState(generateRoomName);
    const [custom, setCustom] = useState("");
    const [own, setOwn] = useState(false);
    const name = own ? custom : combineRoomName(parts);
    useEffect(() => onName(name), [name, onName]);
    return (
        <div className="guild-room-name">
            <p className="guild-kicker">Name your gathering place</p>
            {own ? (
                <Field label="Your room name">
                    <input
                        value={custom}
                        onChange={(e) => setCustom(e.target.value)}
                        maxLength={80}
                        autoComplete="off"
                        required
                    />
                </Field>
            ) : (
                <div className="guild-name-reels">
                    <WordCombo
                        label="Prefix"
                        words={roomPrefixes}
                        optional
                        value={parts.prefix}
                        onChange={(prefix) =>
                            setParts((p) => ({ ...p, prefix }))
                        }
                    />
                    <WordCombo
                        label="Adjective"
                        words={roomAdjectives}
                        optional
                        value={parts.adjective}
                        onChange={(adjective) =>
                            setParts((p) => ({ ...p, adjective }))
                        }
                    />
                    <WordCombo
                        label="Place"
                        words={roomNouns}
                        value={parts.noun}
                        onChange={(noun) => setParts((p) => ({ ...p, noun }))}
                    />
                </div>
            )}
            <output aria-live="polite" className="guild-name-preview">
                {name || "Your room name"}
            </output>
            <div className="guild-actions">
                <button
                    type="button"
                    className="guild-link"
                    onClick={() => setOwn(!own)}
                >
                    {own ? "Use generated name" : "Use my own name"}
                </button>
                {!own && (
                    <button
                        type="button"
                        className="guild-link"
                        onClick={() => setParts(generateRoomName())}
                    >
                        ↻ Shuffle room name
                    </button>
                )}
            </div>
        </div>
    );
}
